import axios from 'axios';
import { getAuth } from 'firebase/auth';
import config from 'config';

const apiUrl = config.apiUrl;

/**
 * Transcribe audio file using OpenAI Whisper API
 * @param {Object} params - Transcription parameters
 * @param {File} params.audioFile - Audio file to transcribe (required)
 * @param {string} params.language - Language code for the audio (optional)
 * @param {string} params.prompt - Prompt to guide the transcription (optional)
 * @returns {Promise} Promise that resolves with transcription text
 */
export const transcribeAudio = async ({ audioFile, language = null, prompt = null }) => {
  const auth = getAuth();

  return new Promise((resolve, reject) => {
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          // Validate audio file
          if (!audioFile) {
            throw new Error('No audio file provided');
          }

          // Validate file type
          const allowedExtensions = ['.mp3', '.mp4', '.mpeg', '.mpga', '.m4a', '.wav', '.webm'];
          const fileName = audioFile.name.toLowerCase();
          const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
          
          if (!hasValidExtension) {
            throw new Error(`Unsupported file type. Allowed types: ${allowedExtensions.join(', ')}`);
          }

          // Check file size (25MB limit)
          const maxSizeInBytes = 25 * 1024 * 1024; // 25MB
          if (audioFile.size > maxSizeInBytes) {
            throw new Error('File size exceeds 25MB limit');
          }

          const token = await user.getIdToken();
          const formData = new FormData();
          
          // Append required audio file
          formData.append('audio_file', audioFile);
          
          // Append optional parameters
          if (language) {
            formData.append('language', language);
          }
          if (prompt) {
            formData.append('prompt', prompt);
          }

          const response = await axios.post(`${apiUrl}/agentic/whisper/transcribe/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`
            },
            timeout: 60000 // 60 second timeout for transcription
          });
          
          console.log('Audio transcription successful:', response.data);
          resolve({
            transcription: response.data.transcription,
            success: response.data.success
          });
        } catch (error) {
          console.error('Error transcribing audio:', error);
          
          let errorMessage = 'Failed to transcribe audio';
          
          if (error.response) {
            console.error('Response error data:', error.response.data);
            console.error('Response error status:', error.response.status);
            errorMessage = error.response.data?.error || errorMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          reject(new Error(errorMessage));
        }
      } else {
        const error = new Error('No authenticated user found');
        console.error(error);
        reject(error);
      }
    });
  });
};

/**
 * Get supported audio formats and API information
 * @returns {Promise} Promise that resolves with API information
 */
export const getTranscriptionInfo = async () => {
  const auth = getAuth();

  return new Promise((resolve, reject) => {
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          
          const response = await axios.get(`${apiUrl}/agentic/whisper/transcribe/`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          console.log('Transcription API info retrieved:', response.data);
          resolve(response.data);
        } catch (error) {
          console.error('Error getting transcription info:', error);
          reject(error);
        }
      } else {
        const error = new Error('No authenticated user found');
        console.error(error);
        reject(error);
      }
    });
  });
};

/**
 * Helper function to validate audio file before upload
 * @param {File} file - Audio file to validate
 * @returns {Object} Validation result with isValid and error message
 */
export const validateAudioFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file type
  const allowedExtensions = ['.mp3', '.mp4', '.mpeg', '.mpga', '.m4a', '.wav', '.webm'];
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
  
  if (!hasValidExtension) {
    return { 
      isValid: false, 
      error: `Unsupported file type. Allowed: ${allowedExtensions.join(', ')}` 
    };
  }

  // Check file size (25MB limit)
  const maxSizeInBytes = 25 * 1024 * 1024; // 25MB
  if (file.size > maxSizeInBytes) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return { 
      isValid: false, 
      error: `File size (${sizeMB}MB) exceeds 25MB limit` 
    };
  }

  return { isValid: true, error: null };
};

// Export default
export default {
  transcribeAudio,
  getTranscriptionInfo,
  validateAudioFile
}; 